import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AutoCompleteDestination, InputDestinationPlace } from '@shared/base-search-bar/entities/autocomplete-destionation';
import { Observable, map, switchMap, tap, take } from 'rxjs';
import { isEmpty, isNullOrUndefined } from '@app/util/util';
import { AmplitudeEventParams } from '@app/modules/amplitude/interfaces/amplitude-events';
import { AMPLITUDE_EVENTS, EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import { ChangeInputPayload } from '@shared/base-search-bar/entities/search-input-payload';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { triggerAmplitudeEventApp } from '@amplitude/helpers/gtm-functions';
import * as Avo from '@avo/Avo';
import { EventProperties } from '@avo/interface/trigger-props';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { AvoService } from '@avo/services/avo.service';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { SearchType } from '@search-bar/entities/search-type';
import { DateCalendarField } from '@design-system/components/organisms/date-calendar/entities/date-calendar.types';

@Component({
  selector: 'app-hotel-search-abstract',
  template: '',
  standalone: false
})
export class HotelsSearchAbstractComponent {

  @Input() showHotelDetail: boolean;
  @Input() source: EVENT_SOURCE | string;
  @Input() searchType: SearchType;
  
  @Output() showMobileCalendarEvent: EventEmitter<CalendarField> = new EventEmitter<CalendarField>();
  
  protected arrivalDate: Date;
  protected departureDate: Date;
  protected isSubmited = false;
  protected placeSelectedItem: InputDestinationPlace;
  protected showCalendarInput = false;
  protected activeCalendarType: CalendarField = CalendarField.departure;
  protected AvoDispatch = new AvoDispatchOrchestrator();

  public dateCalendarField: DateCalendarField = 'left';
  
  constructor(
    protected baseSearchbarfacade: BaseSearchBarFacade,
    protected avoService: AvoService,
    protected amplitude: PropertiesAmplitudeService
  ) {
  }

  get autocompleteDestinationPlaces$(): Observable<AutoCompleteDestination> {
    return this.baseSearchbarfacade.autocompleteDestinationPlaces$;
  }

  get destinationHotelPlace$(): Observable<InputDestinationPlace> {
    return this.baseSearchbarfacade.hotelDestinationPlace$;
  }

  get isLoadingAutocompletePlaces$(): Observable<boolean> {
    return this.baseSearchbarfacade.isLoadingAutocompletePlaces$;
  }

  get calendarField(): typeof CalendarField {
    return CalendarField;
  }

  get isDepartureDateEmpty(): boolean {
    return isNullOrUndefined(this.departureDate) && this.isSubmited;
  }

  get isArrivalDateEmpty(): boolean {
    return isNullOrUndefined(this.arrivalDate) && this.isSubmited;
  }

  get isPlaceSelectedEmpty(): boolean {
    return (isNullOrUndefined(this.placeSelectedItem) || isEmpty(this.placeSelectedItem)) && this.isSubmited;
  }

  get isHotel(): boolean {
    return this.searchType === SearchType.HOME_HOTELS || 
           this.searchType === SearchType.HOTELS_RESULTS ||
           this.searchType === SearchType.HOTELS_DETAIL;
  }

  get searchBy(): SearchBy {
    return this.isHotel ? SearchBy.HOTELS : SearchBy.BYHOURS;
  }

  public setCalendarField(type: DateCalendarField): void {
    this.showCalendarInput = true;
    this.dateCalendarField = type;
  }

  public placeInputChanged(input: ChangeInputPayload): void {
    if (input.isEmpty) {
      this.baseSearchbarfacade.resetAutocomplete();
    } else {
      this.baseSearchbarfacade.searchDestinationPlaces(input.value);
    }
  }

  public showMobileCalendar(type: CalendarField): void {
    this.showMobileCalendarEvent.emit(type);
  }

  public popularSelectedEvent(props: EventProperties): void {
    this.amplitudeEvent({ type: AMPLITUDE_EVENTS.SELECT_TRAVEL_POPULAR_DESTINATIONS, props });
  }

  public locationNotFoundEvent(props: EventProperties): void {
    this.avoService.getEventTriggerProps$()
      .pipe(
        tap(trigger => {
          this.AvoDispatch.travelLocationNotFound({
            ...this.avoService.avoBaseProperties(trigger),
            travelProductType: props.TRAVEL_PRODUCT_TYPE as string,
            controlId: props.CONTROL_ID as string,
            searchQuery: props.SEARCH_QUERY as string,
          })
        }
        )).subscribe().unsubscribe();
  }

  public selectPlace(place: InputDestinationPlace): void {
    this.baseSearchbarfacade.searchData$.pipe(
      tap(search => {
        this.baseSearchbarfacade.selectHotelDestinationPlace(place);
        this.placeSelectedItem = place;
        this.baseSearchbarfacade.saveSearchData(search, SearchBy.HOTELS);
      })).subscribe().unsubscribe();
  }

  public clearAutocompletePlaces(): void {
    this.baseSearchbarfacade.resetAutocomplete();
  }

  public amplitudeEvent(params: AmplitudeEventParams): void {
    this.avoService.getEventTriggerProps$(params.type, params.props)
      .pipe(
        tap(trigger => triggerAmplitudeEventApp(trigger))
      ).subscribe()
      .unsubscribe();
  }

  protected registerViewOrSelectAmplitudeEvent(source: EVENT_SOURCE | string, isClickAction: boolean = false): void {
    if (isClickAction) {
      this.amplitude.searchProperties$(this.searchBy).pipe(
        take(1), map(searchProps => ({
          _source: this.showHotelDetail ? EVENT_SOURCE.DETAIL : source, searchProps
        })),
        switchMap(({ searchProps, _source }) => this.avoService.getEventTriggerProps$(
          AVO_EVENTS.SELECT_TRAVEL_SEARCH, { SOURCE: _source }, searchProps
        )),
        tap(trigger =>
          this.AvoDispatch.selectTravelSearch({
            ...this.avoService.avoBaseProperties(trigger),
            source: this.source as Avo.SourceValueType,
          })
        )
      ).subscribe().unsubscribe();
    } else {
      this.avoService.getEventTriggerProps$(
        AVO_EVENTS.VIEW_TRAVEL_SEARCH_SCREEN,
        { SOURCE: source, TRAVEL_PRODUCT_TYPE: this.searchBy }
      ).pipe(
        tap(trigger =>
          this.AvoDispatch.viewTravelSearchScreen({
            ...this.avoService.avoBaseProperties(trigger),
            source: this.source as Avo.SourceValueType,
            travelProductType: this.searchBy
          })
        )
      ).subscribe().unsubscribe();
    }
  }
}
